import { useCallback, useState, useRef, useEffect } from "react";
import ClaudeService from "../../services/claudeService";
import { LUMEN_SYSTEM_PROMPT } from "../../services/prompts";
import { LumenExpandIcon, LumenMinimizeIconSmall,LumenCollapseIconSmall }  from "../../lib/Icons/LumenIcon.js";
import TypewriterText from "../../components/TypewriterText";
import { useAutoResizeTextarea } from "../../hooks/useAutoResizeTextarea";
import { createPasteFallbackHandler } from "../../utils/pasteFallback";
import LumenDockLayout from "./LumenDockLayout";
import { lastRoomIdFromSession, locationFromRoomId, roomNameFromLocation, pathNamesFromRoomId } from "../../lib/worldJson";
import { formatDeskDocuments, formatDeskResults, chatHistoryForModel, stripReceiptEcho, isDeskReceiptSuccess, findDeskUpdate, softenPendingReply, matchDeskChoiceReply, describeDeskReceipt } from "../../lib/deskWrites";
import { withDeskItemRetry } from "../../services/lumenDeskRetry";
import { withCalendarRetry } from "../../services/lumenCalendarRetry";
import { toDateKey } from "../../lib/plannerItems";
import { describeCalendarForLumen, describeToday } from "../../lib/lumenCalendar";
import CalendarReceiptLine from "./CalendarReceiptLine";
import { describeMyDayNow } from "../../lib/dayPlanProposal";
import DeskReceiptLine from "./DeskReceiptLine";

const CHAT_INPUT_MAX_HEIGHT = 120;
const MINIMIZED_CHAT_INPUT_MAX_HEIGHT = 60;

const CONFIRM_RE = /^(yes|yeah|yep|yup|sure|ok|okay|looks good|sounds good|sounds right|perfect|do it|go ahead|save it|correct|right|exactly|love it|great|that works|that'?s right|build it)/i;
const REVISION_SIGNAL_RE = /\b(but|except|change|instead|actually|not)\b/i;
const EXPLICIT_REVISION_RE = /\b(change|rename|move|swap|replace|remove|add|take out|put|make it|call it|instead|different|not that|wrong|should be|meant to|actually)\b/i;
const QUESTION_RE = /\?|^(what|how|why|does|is)\b/i;
const CONFUSION_RE = new RegExp(
  // Group 1: negated comprehension verbs
  "\\b(don'?t|didn'?t|doesn'?t|can'?t)\\s+(get|understand|follow|see)\\b" +
  "|\\bdoesn'?t\\s+make\\s+sense\\b" +
  "|\\bnot\\s+(getting|understanding|following)\\b" +
  // Group 2: uncertainty words
  "|\\b(not\\s+sure|unsure|unclear|confused|confusing|lost|uncertain)\\b" +
  // Group 3: still + signal (requires second token to avoid false positives)
  "|\\bstill\\s+(don'?t|not|confused|lost|unclear|unsure)\\b" +
  // Group 4: explanation requests
  "|\\b(explain|elaborate)\\b" +
  "|\\bbreak\\s+(it|that|this)\\s+down\\b" +
  "|\\bspell\\s+it\\s+out\\b" +
  "|\\bsimplif" +
  "|\\bin\\s+plain\\b" +
  "|\\bwhat\\s+(do|does|did)\\s+you\\s+mean\\b" +
  "|\\bwhat\\s+does\\s+that\\s+mean\\b" +
  // Group 5: start-anchored filler/hesitation
  "|^huh\\b" +
  "|^(wait,?\\s*what|hold\\s+on|sorry,?\\s*what)\\b",
  "i"
);
function isConfirmation(text) {
  const t = text.trim();
  return CONFIRM_RE.test(t) && !REVISION_SIGNAL_RE.test(t) && !t.includes("?");
}
function isQuestion(text) {
  const t = text.trim();
  return QUESTION_RE.test(t) || CONFUSION_RE.test(t);
}
function isRevision(text) {
  const newText = text.trim();
  return EXPLICIT_REVISION_RE.test(newText) && !isConfirmation(newText);
}

function describeWorldForPreview(parsedWorld) {
  const islandDescs = [];
  for (const district of parsedWorld.districts || []) {
    for (const island of district.islands || []) {
      const buildingDescs = (island.buildings || []).map(b => {
        const floors = b.floors || [];
        const allRooms = floors.flatMap(f => (f.rooms || []).map(r => r.name));
        if (floors.length > 1) {
          const floorDescs = floors.map(f =>
            `${f.name} (${(f.rooms || []).map(r => r.name).join(", ")})`
          ).join(", ");
          return `a Building called ${b.name} with stages: ${floorDescs}`;
        }
        return `a Building called ${b.name} with rooms for ${allRooms.join(", ")}`;
      });
      islandDescs.push(`an Island called ${island.name}, with ${buildingDescs.join(", plus ")}`);
    }
  }
  return `Here's what I'd build: ${islandDescs.join("; and ")}. Sound right?`;
}

const WHITEBOARD_NOTE = "\n\nThe user is standing at the whiteboard right now. Answer about the board. If Whiteboard Contents has something on it, say what it suggests they are doing and offer one or two concrete kinds of help for exactly that, such as an itinerary for a trip. Engage with the board first and bring in the room's project only if the board is clearly part of it. Never question whether it belongs, never suggest clearing or replacing it, and never suggest it landed by mistake. If they ask to change or remove something you added, replace or remove it by its id instead of adding another.";

const SURFACE_NAMES = {
  whiteboard: "the whiteboard",
  desk: "the desk",
  bookshelf: "the bookshelf",
  calendar: "my calendar",
};

export function LumenCard({ dock, roomName, buildingName, islandName, floorName, activeSurface, whiteboardContents, projectOverview, surfaceState, onSurfaceUpdate, onDeskAction, height, minimized, sidePanel, onToggleMinimize, isMaximized, onUnmaximize, onMaximize, onBrainDump, currentMode, userProfile, logEvent, focusMode, onFocusMode, onExitFocusMode, onClose, hasWorld, previousSession, currentRoomActivity, justBuiltWorld, onJustBuiltWorldDismiss, onNavigateToLastRoom, onOpenMap, onOpenDesk, messages, setMessages, exchangeCount, setExchangeCount, onStartFresh, onParseWorld, onSaveWorld, world, externalThinking = false, welcomeBack = false, lastWorkedOn = null, onWelcomeBackDismiss, visitedRoomThisSession = false, deskChoices = null, onUsage, capState = "ok", onContinueSession }) {
  const [input, setInput]           = useState("");
  const [isThinking, setIsThinking] = useState(false);
  // externalThinking: parent-driven loader (e.g. desk auto-draft after room welcome)
  const showThinking = isThinking || externalThinking;
  const [isBuildingWorld, setIsBuildingWorld] = useState(false);
  const [pendingParsedWorld, setPendingParsedWorld] = useState(null);
  // focusMode is now controlled externally when maximized, local otherwise
  const [localFocusMode, setLocalFocusMode] = useState(false);
  const isFocused = focusMode !== undefined ? focusMode : localFocusMode;
  const [explainerMode, setExplainerMode] = useState(false);
  const [havenIntroSeen, setHavenIntroSeen] = useState(false);
  /** "Start somewhere else" was clicked out in the world — reveal the start-options trio. */
  const [startOptionsOpen, setStartOptionsOpen] = useState(false);
  // Moving somewhere new ends the "start somewhere else" detour.
  useEffect(() => { setStartOptionsOpen(false); }, [roomName]);
  const enterFocus = () => onFocusMode ? onFocusMode() : setLocalFocusMode(true);
  const exitFocus  = () => onExitFocusMode ? onExitFocusMode() : setLocalFocusMode(false);
  const inputRef = useRef(null);
  const focusAfterExpandRef = useRef(false);
  useEffect(() => {
    if (dock?.collapsed || !focusAfterExpandRef.current) return;
    focusAfterExpandRef.current = false;
    inputRef.current?.focus();
  }, [dock?.collapsed]);
  const messagesContainerRef = useRef(null);
  const attachMessagesContainer = useCallback((node) => {
    messagesContainerRef.current = node;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
    requestAnimationFrame(() => { node.scrollTop = node.scrollHeight; });
  }, []);
  const prevMessagesLenRef = useRef(-1);
  const prevLastMsgRef = useRef(null);
  const reloadPendingRef = useRef(false);
  const mountedMsgCountRef = useRef(messages.length);
  const welcomeAnchorRef   = useRef(null);
  const prevWelcomeBackRef = useRef(false);
  useAutoResizeTextarea(inputRef, input, sidePanel ? MINIMIZED_CHAT_INPUT_MAX_HEIGHT : CHAT_INPUT_MAX_HEIGHT);

  // Not currently in a room (Haven/home) — resolve the last room's name for a contextual
  // greeting even when last_activity was cleared by a clean session end.
  const lastRoomId = lastRoomIdFromSession(previousSession);
  const lastRoomLocation = lastRoomId ? locationFromRoomId(world?.districts, lastRoomId) : null;
  const lastRoomName = previousSession?.last_activity?.room_name
    || (lastRoomLocation?.roomId ? roomNameFromLocation(world?.districts, lastRoomLocation) : null);

  const inNamedRoom = roomName && roomName !== "My Room";
  const leftOffRoom = inNamedRoom ? roomName : lastRoomName;
  const greetName = userProfile?.avatar?.name ? `, ${userProfile.avatar.name}` : "";
  const workedOnLabel =
    lastWorkedOn && lastWorkedOn.trim().toLowerCase() !== (leftOffRoom || "").trim().toLowerCase()
      ? lastWorkedOn
      : null;
  const nothingStartedYet = inNamedRoom && !workedOnLabel;

  const freshSession = messages.length === 1 && !!messages[0]?.isNewSession;
  const showGetStartedPair = (welcomeBack && nothingStartedYet) || freshSession;
  const showRoomPair = !showGetStartedPair && inNamedRoom;
  const showWorldPair = !showGetStartedPair && !inNamedRoom && !!previousSession?.last_room_id;
  const hasPair = hasWorld && (showGetStartedPair || showRoomPair || showWorldPair);
  const showPair = hasPair && (messages.length === 0 || welcomeBack || freshSession) && !startOptionsOpen;
  const showStartOptions = startOptionsOpen || (messages.length === 0 && !showPair);
  const lastPath = !inNamedRoom && lastRoomId ? pathNamesFromRoomId(world?.districts, lastRoomId) : null;
  const lastBuilding = previousSession?.last_activity?.building_name || lastPath?.buildingName || null;
  const lastIsland = previousSession?.last_activity?.island_name || lastPath?.islandName || null;
  const worldTrail = [leftOffRoom, lastBuilding, lastIsland].filter(Boolean).join(", ");

  if (welcomeBack && !prevWelcomeBackRef.current) welcomeAnchorRef.current = messages.length;
  if (!welcomeBack) welcomeAnchorRef.current = null;
  prevWelcomeBackRef.current = welcomeBack;
  const showWelcomeBubble =
    welcomeBack && messages.length > 0 && welcomeAnchorRef.current !== null && messages.length <= welcomeAnchorRef.current;

  let welcomeBackText;
  if (nothingStartedYet) {
    welcomeBackText = `Welcome back to ${roomName}${greetName}. You haven't started on anything yet in here.`;
  } else if (inNamedRoom) {
    welcomeBackText = `Welcome back to ${roomName}${greetName}.${workedOnLabel ? ` You left off on ${workedOnLabel}.` : ""}`;
  } else if (worldTrail) {
    const greeting = visitedRoomThisSession ? "" : `Welcome back${greetName}. `;
    welcomeBackText = workedOnLabel
      ? `${greeting}You were last working on ${workedOnLabel}, in ${worldTrail}.`
      : `${greeting}You were last in ${worldTrail}.`;
  } else {
    welcomeBackText = `Welcome back${greetName}.`;
  }

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const isFirstRun = prevMessagesLenRef.current === -1;
    const prevLen = prevMessagesLenRef.current;
    const prevLast = prevLastMsgRef.current;
    const lastMsg = messages.length ? messages[messages.length - 1] : null;
    prevMessagesLenRef.current = messages.length;
    prevLastMsgRef.current = lastMsg;

    if (messages.length === 0) {
      reloadPendingRef.current = true;
      return;
    }
    const reloadLanded = reloadPendingRef.current;
    reloadPendingRef.current = false;
    if (isFirstRun || reloadLanded) {
      container.scrollTop = container.scrollHeight;
      return;
    }

    if (messages.length > prevLen || lastMsg !== prevLast || showThinking) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [messages, showThinking]);

  const buildPrompt = (userProfile) => {
    const profileText = userProfile ? [
      `Procrastination type: ${userProfile.declared_procrastination_type || "unknown"}`,
      `Chronotype: ${userProfile.declared_chronotype || "unknown"}`,
      `Task initiation barrier: ${userProfile.declared_initiation_barrier || "unknown"}`,
      `Hyperfocus pattern: ${userProfile.declared_hyperfocus_pattern || "unknown"}`,
      `RSD sensitivity: ${userProfile.declared_rsd_sensitivity || "medium"}`,
      `Structure preference: ${userProfile.declared_structure_preference || "on_demand"}`,
      userProfile.avatar?.name ? `User's name: ${userProfile.avatar.name}` : "",
    ].filter(Boolean).join("\n") : "No profile yet. Use defaults: medium structure, medium RSD sensitivity, adaptive.";

    return LUMEN_SYSTEM_PROMPT
      .replaceAll("{{ROOM_NAME}}", roomName || "My Room")
      .replaceAll("{{BUILDING_NAME}}", buildingName || "My Building")
      .replaceAll("{{ISLAND_NAME}}", islandName || "My Island")
      .replaceAll("{{FLOOR_NAME}}", floorName || "My Floor")
      .replaceAll("{{ACTIVE_SURFACE}}", activeSurface || "none")
      .replaceAll("{{CURRENT_MODE}}", isFocused ? "focus" : (explainerMode && !hasWorld ? "explainer" : (currentMode || "work")))
      .replaceAll("{{WHITEBOARD_CARDS}}", whiteboardContents || surfaceState.whiteboardCards?.map(c => c.content).join(", ") || "empty")
      .replaceAll("{{BULLETIN_ITEMS}}", surfaceState.bulletinItems?.map(c => c.content).join(", ") || "empty")
      .replaceAll("{{CALENDAR_EVENTS}}", describeCalendarForLumen(surfaceState.calendarEvents, toDateKey()))
      .replaceAll("{{MY_DAY}}", describeMyDayNow(surfaceState.plannerItems))
      .replaceAll("{{TODAY}}", describeToday())
      .replaceAll("{{DESK_DOCUMENTS}}", formatDeskDocuments(surfaceState.deskDocuments))
      .replaceAll("{{DESK_RESULTS}}", formatDeskResults(messages))
      .replaceAll("{{USER_PROFILE}}", profileText)
      .replaceAll("{{SESSION_HISTORY}}", currentRoomActivity?.last_summary || "none")
      .replaceAll("{{HAS_WORLD}}", hasWorld ? "yes" : "no")
      .replaceAll("{{PROJECT_OVERVIEW}}", projectOverview || "none");
  };

  const send = () => sendText(input);

  const sendChatMessage = async(e) => {
    e.preventDefault();
    send();
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const CHIPS = hasWorld
    ? ["✦ Brain Dump — let's build my world", "Walk me through it manually", "What can Lumen help me with?"]
    : ["✦ Brain Dump — let's build my world", "What can Lumen help me with?"];

  const repairFalsePastTense = (text, writeConfirmed) => {
    if (writeConfirmed) return text;
    return text
      .replace(/\bI've added\b/gi,   "I'll add")
      .replace(/\bI've put\b/gi,     "I'll put")
      .replace(/\bI've created\b/gi, "I'll create")
      .replace(/\bI've saved\b/gi,   "I'll save")
      .replace(/\bI've built\b/gi,   "I'll build")
      .replace(/\bI've placed\b/gi,  "I'll place")
      .replace(/\bI've sent\b/gi,    "I'll send")
      .replace(/\bI've updated\b/gi, "I'll update")
      .replace(/\byou'?ve got (.+?) already\b/gi, "I'll add $1")
      .replace(/\bthere(?:'s| is) already (?:a |an |the )?/gi, "I'll add ");
  };

  const sendText = async (text) => {
    if (!text?.trim() || showThinking || capState === "full") return;
    onWelcomeBackDismiss?.(); // user engaged — the return-to-session prompt has served its purpose
    const userMsg = { role: "user", content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");

    // ── World preview response (confirmation or revision) ──
    if (pendingParsedWorld) {
      setExchangeCount(c => c + 1);
      if (isConfirmation(text)) {
        // ── Save path ──
        setIsBuildingWorld(true);
        setMessages(prev => [...prev, { role: "assistant", content: "Building your world...", _buildingWorld: true }]);
        let saveResult;
        try {
          saveResult = await onSaveWorld(pendingParsedWorld);
        } finally {
          setIsBuildingWorld(false);
          setMessages(prev => prev.filter(m => !m._buildingWorld));
          setPendingParsedWorld(null);
        }
        const isFirstBuild = !world?.districts?.length;
        const msg = saveResult?.ok === false
          ? (saveResult.error || "Something went wrong. Try Brain Dump instead.")
          : isFirstBuild ? "Your world is ready. Open Map to explore." : "Your world is updated. Open Map to explore.";
        setMessages(prev => [...prev, { role: "assistant", content: msg }]);
      } else if (isQuestion(text)) {
        // ── Question path — answer conversationally, keep pendingParsedWorld alive ──
        setIsThinking(true);
        const previewContext = `\n\nCONTEXT: The user is reviewing a proposed world structure before confirming. The proposed structure is: "${describeWorldForPreview(pendingParsedWorld)}". Answer their question about it conversationally. After answering, they can confirm the structure or suggest changes. CRITICAL: You must still respond with valid JSON in the exact format {chat_response, surface_updates, desk_action}. Set desk_action to null and surface_updates to []. Never return plain prose — the JSON wrapper is always required.`;
        try {
          const result = await ClaudeService.sendStructuredMessage({
            system: buildPrompt(userProfile) + previewContext,
            messages: chatHistoryForModel([...messages, userMsg]),
            model: "claude-sonnet-4-6",
            maxTokens: 4000,
          });
          const reply = result.success
            ? (stripReceiptEcho(result.data?.chat_response) || "...")
            : "I had trouble reaching the server. Give me a moment and try again.";
          setMessages(prev => [...prev, { role: "assistant", content: reply }]);
          // pendingParsedWorld intentionally not cleared — user can still confirm or revise
        } catch {
          setMessages(prev => [...prev, { role: "assistant", content: "I had trouble reaching the server. Give me a moment and try again." }]);
        } finally {
          setIsThinking(false);
        }
      } else if (isRevision(text)) {
        // ── Revision path — re-parse with full conversation including feedback ──
        setIsBuildingWorld(true);
        setMessages(prev => [...prev, { role: "assistant", content: "Revising...", _buildingWorld: true }]);
        let revised;
        let parseError = false;
        try {
          const chatHistory = [...messages, userMsg]
            .map(m => ({ role: m.role, content: m.content }));
          revised = await onParseWorld(chatHistory, world);
        } catch {
          parseError = true;
        } finally {
          setIsBuildingWorld(false);
          setMessages(prev => prev.filter(m => !m._buildingWorld));
        }
        if (parseError || !revised?.districts) {
          setMessages(prev => [...prev, { role: "assistant", content: "I didn't quite catch that. Want to confirm this structure, ask me something about it, or tell me what to change?" }]);
          // pendingParsedWorld intentionally not cleared — original preview stays live
        } else {
          setPendingParsedWorld(revised);
          setMessages(prev => [...prev, { role: "assistant", content: describeWorldForPreview(revised) }]);
        }
      } else {
        //  Unrecognised input — nudge without touching the pending preview
        setMessages(prev => [...prev, { role: "assistant", content: "Want to confirm this structure, ask me something about it, or tell me what to change?" }]);
      }
      return;
    }

    // A short typed answer to an open desk question ("update", "keep both", "skip") is the same
    // as clicking that button — sending it to Lumen would start a new, different desk write.
    const typedChoice = deskChoices?.pending ? matchDeskChoiceReply(text, deskChoices.pending.kind) : null;
    if (typedChoice) {
      setIsThinking(true);
      try {
        const receipt = await deskChoices.resolve(typedChoice);
        if (receipt) setMessages(prev => [...prev, { role: "assistant", content: describeDeskReceipt(receipt) }]);
      } finally { setIsThinking(false); }
      return;
    }

    setIsThinking(true);
    const newCount = exchangeCount + 1;
    setExchangeCount(newCount);

    const atOtherSurface = Boolean(activeSurface) && activeSurface !== "desk";
    const extra = newCount >= 2 && !atOtherSurface
      ? "\n\nIMPORTANT: Draft something now in HTML and send to desk. Then write a warm chat_response that continues the conversation - never go silent after drafting."
      : "";

    const surfaceNote = activeSurface === "whiteboard" ? WHITEBOARD_NOTE : "";

    const callOptions = {
      system: buildPrompt(userProfile) + extra + surfaceNote,
      messages: chatHistoryForModel([...messages, userMsg]),
      model: "claude-sonnet-4-6",
      maxTokens: 2000,
    };

    try {
      let result = await ClaudeService.sendStructuredMessage(callOptions);

      // Retry once on rate-limit / overload responses before surfacing any error
      if (!result.success && (result.status === 429 || result.status === 529)) {
        await new Promise(r => setTimeout(r, 2000));
        result = await ClaudeService.sendStructuredMessage(callOptions);
      }

      if (!result.success) {
        setMessages(prev => [...prev, { role: "assistant", content: "I had trouble reaching the server. Give me a moment and try again." }]);
        return;
      }

      onUsage?.(result.usage);
      const parsed = result.data;

      // ── Build world from conversation — parse only, then preview ──
      if (parsed.desk_action === "build_world" && onParseWorld) {
        const safeResponse = repairFalsePastTense(parsed.chat_response || "...", false);
        setMessages(prev => [...prev, { role: "assistant", content: safeResponse }]);
        const chatHistory = [...messages, userMsg]
          .map(m => ({ role: m.role, content: m.content }));
        setIsBuildingWorld(true);
        setMessages(prev => [...prev, { role: "assistant", content: "Mapping your world...", _buildingWorld: true }]);
        let parseResult;
        try {
          parseResult = await onParseWorld(chatHistory, world);
        } finally {
          setIsBuildingWorld(false);
          setMessages(prev => prev.filter(m => !m._buildingWorld));
        }
        setPendingParsedWorld(parseResult);
        setMessages(prev => [...prev, { role: "assistant", content: describeWorldForPreview(parseResult) }]);
        return;
      }

      let writeConfirmed = false;
      // The receipt is the app's answer to "did it land?" — the reply waits for it.
      let deskReceipt = null;
      let calendarReceipts = [];
      const reply = await withCalendarRetry(callOptions, await withDeskItemRetry(callOptions, parsed, { activeSurface }));
      const surfaceUpdates = reply.surface_updates || [];
      const deskUpdate = findDeskUpdate(surfaceUpdates);
      // deskWriteUnsent: Lumen's reply claimed a desk change it never sent, even after being asked again.
      const claimsDeskWrite = reply.desk_action === "new_doc" || reply.desk_action === "append" || reply.deskWriteUnsent === true;
      if (surfaceUpdates.length || claimsDeskWrite) {
        const results = surfaceUpdates
          .filter(u => u !== deskUpdate)
          .map(u => onSurfaceUpdate(u));
        const nonDeskOk = results.every(r => r?.ok !== false);
        calendarReceipts = results.map(r => r?.calendarReceipt).filter(Boolean);

        if (deskUpdate || claimsDeskWrite) {
          deskReceipt = onDeskAction
            ? await onDeskAction(reply.desk_action || "new_doc", surfaceUpdates)
            : { status: "failed", reason: "no_desk_update" };
        }
        writeConfirmed = nonDeskOk && (!deskReceipt || isDeskReceiptSuccess(deskReceipt));
      }
      if (reply.calendarWriteUnsent) {
        calendarReceipts = [...calendarReceipts, {
          ...(reply.calendarWriteUnsent === "my_day" ? { kind: "plan" } : {}), action: "failed", reason: "not_sent",
        }];
        writeConfirmed = false;
      }

      const said = stripReceiptEcho(reply.chat_response);
      const replyText = deskReceipt?.status === "pending" ? softenPendingReply(said) : said;
      const safeResponse = repairFalsePastTense(replyText || "...", writeConfirmed);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: safeResponse,
        ...(deskReceipt ? { deskReceipt } : {}),
        ...(calendarReceipts.length ? { calendarReceipts } : {}),
      }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "I had trouble reaching the server. Give me a moment and try again." }]);
    } finally { setIsThinking(false); }
  };

  const headerRow = (
        <div style={{ padding: "10px 14px", borderBottom: minimized ? "none" : "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 8, flexShrink: 0, background: "white" }}>
        <div style={{ width: 26, height: 26, borderRadius: "50%", background: showThinking ? "rgba(77,184,181,0.2)" : "rgba(77,184,181,0.1)", border: "1px solid rgba(77,184,181,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#4DB8B5", flexShrink: 0 }}>✦</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", fontFamily: "'DM Sans', sans-serif" }}>Lumen</div>
          {!minimized && <div style={{ fontSize: 10, color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>{roomName || "My Room"}</div>}
        </div>
        {showThinking && !isBuildingWorld && !minimized && <div style={{ fontSize: 16, color: "#4DB8B5", letterSpacing: 2 }}>···</div>}
        {/* Brain Dump button */}
        {!minimized && onBrainDump && (
          <button onClick={() => { setExplainerMode(false); onBrainDump?.(); }} style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(77,184,181,0.08)", border: "1px solid rgba(77,184,181,0.2)", color: "#4DB8B5", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap" }}>⚡ Brain Dump</button>
        )}
        {/* Focus mode button */}
        {isMaximized && !minimized && (
          <button onClick={enterFocus} style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.08)", color: "#64748B", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Focus</button>
        )}
        {isMaximized
          ? (
            <button
              type="button"
              onClick={onToggleMinimize}
              title="Back to the dock"
              style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 24, padding: "4px 8px", lineHeight: 1, fontWeight: 700 }}
            >
              <LumenMinimizeIconSmall/>
            </button>
          )
          : onMaximize && (
            <button onClick={onMaximize} title="Expand Lumen" style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 14, padding: "0 2px", lineHeight: 1 }}>
              <LumenExpandIcon className='transition-transform duration-300 rotate-180' />
            </button>
          )
        }
        {/* Collapse button — right side, matching expand icon weight */}
        {sidePanel && onToggleMinimize && !isMaximized && (
          <button
            type="button"
            onClick={onToggleMinimize}
            title="Close Lumen"
            style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", padding: "0 2px", lineHeight: 1, display: "flex", alignItems: "center" }}
          >
            <LumenCollapseIconSmall />
          </button>
        )}
      </div>
  );

  const messagesArea = (
          <div ref={attachMessagesContainer} style={{ flex: 1, overflow: "auto", padding: "14px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
            {messages.length === 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "8px 4px" }}>
                {hasWorld && (
                  <div style={{ alignSelf: "flex-start", maxWidth: "85%", background: "#F1F5F9", borderRadius: "12px 12px 12px 4px", padding: "12px 14px", fontSize: 13, lineHeight: 1.7, color: "#1E293B", fontFamily: "'DM Sans', sans-serif" }}>
                    {/* Same card text whether or not this thread has history, so the world-level
                        greeting doesn't lose its path just because Haven has no saved messages. */}
                    {inNamedRoom
                      ? (currentRoomActivity ? `Welcome back to ${roomName}.` : `Welcome to the ${roomName} room.`)
                      : welcomeBackText}
                  </div>
                )}
                {!hasWorld ? (
                  <div style={{ alignSelf: "flex-start", maxWidth: "90%", background: "#F1F5F9", borderRadius: "12px 12px 12px 4px", padding: "12px 14px", fontSize: 13, lineHeight: 1.7, color: "#1E293B", fontFamily: "'DM Sans', sans-serif" }}>
                    <p style={{ margin: "0 0 10px" }}>Welcome to NeuroQuest. I'm Lumen, your guide here.</p>
                    <p style={{ margin: "0 0 10px" }}>I turn the stuff in your head into a place you can walk through, instead of tabs, files, and folders you dig through. A project, a trip you're planning, whatever's taking up space.</p>
                    <p style={{ margin: "0 0 10px" }}>That's Haven behind me, your world center. Everything outside your work lives there.</p>
                    <p style={{ margin: 0 }}>What do you want to do first?</p>
                  </div>
                ) : justBuiltWorld ? (
                  <>
                    <div style={{ alignSelf: "flex-start", maxWidth: "85%", background: "#F1F5F9", borderRadius: "12px 12px 12px 4px", padding: "12px 14px", fontSize: 13, lineHeight: 1.7, color: "#1E293B", fontFamily: "'DM Sans', sans-serif" }}>
                      Your world's built.
                    </div>
                    <div style={{ alignSelf: "flex-start", maxWidth: "85%", background: "#F1F5F9", borderRadius: "12px 12px 12px 4px", padding: "12px 14px", fontSize: 13, lineHeight: 1.7, color: "#1E293B", fontFamily: "'DM Sans', sans-serif" }}>
                      Take a look around, or head into your first room when you're ready. What do you want to do?
                    </div>
                  </>
                ) : (
                  <div style={{ alignSelf: "flex-start", maxWidth: "85%", background: "#F1F5F9", borderRadius: "12px 12px 12px 4px", padding: "12px 14px", fontSize: 13, lineHeight: 1.7, color: "#1E293B", fontFamily: "'DM Sans', sans-serif" }}>
                    {roomName && roomName !== "My Room"
                      ? (currentRoomActivity
                        ? `What do you want to focus on in ${roomName} today?`
                        : "You haven't worked in this room before. Where do you want to start?")
                      : (previousSession
                        ? (lastRoomName ? `Pick up in ${lastRoomName}, or start something new?` : "Where do you want to start today?")
                        : "What do you want to work on?")
                    }
                  </div>
                )}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%", background: m.role === "user" ? "#4DB8B5" : "#F1F5F9", color: m.role === "user" ? "white" : "#1E293B", borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px", padding: "8px 12px", fontSize: 13, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif", whiteSpace: "pre-wrap" }}>
                <TypewriterText text={m.content} animate={i === messages.length - 1 && m.role === "assistant" && !m.isRestored && i >= mountedMsgCountRef.current} />
                {m.deskReceipt && <DeskReceiptLine receipt={m.deskReceipt} deskChoices={deskChoices} />}
                {m.calendarReceipts && <CalendarReceiptLine receipts={m.calendarReceipts} />}
              </div>
            ))}
            {showWelcomeBubble && (
              <div style={{ alignSelf: "flex-start", maxWidth: "85%", background: "#F1F5F9", borderRadius: "12px 12px 12px 4px", padding: "12px 14px", fontSize: 13, lineHeight: 1.7, color: "#1E293B", fontFamily: "'DM Sans', sans-serif" }}>
                {welcomeBackText}
              </div>
            )}
            {showThinking && !isBuildingWorld && <div style={{ alignSelf: "flex-start", background: "#F1F5F9", borderRadius: "12px 12px 12px 4px", padding: "8px 14px", color: "#4DB8B5", fontSize: 16, letterSpacing: 2 }}>···</div>}
          </div>
  );

  const pairButtons = showPair && (
            <div style={{ padding: dock ? 0 : "0 12px 10px", display: "flex", flexDirection: dock ? "row" : "column", flexWrap: "wrap", gap: dock ? 8 : 6 }}>
              {showGetStartedPair
                ? <>
                    <button onClick={() => {
                        onWelcomeBackDismiss?.();
                        const where = SURFACE_NAMES[activeSurface];
                        sendText(where ? `Help me get started with ${where}` : "Help me get started");
                      }}
                      style={{ padding: "11px 14px", borderRadius: 10, background: "#4DB8B5", border: "none", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                      Help me get started
                    </button>
                    <button onClick={() => {
                        onWelcomeBackDismiss?.();
                        setMessages(prev => [...prev, { role: "assistant", content: "Ok, have a look around. I'm here if you have any questions." }]);
                      }}
                      style={{ padding: "11px 14px", borderRadius: 10, background: "white", border: "1.5px solid #4DB8B5", color: "#4DB8B5", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                      Let me explore
                    </button>
                  </>
                : showRoomPair
                ? <>
                    <button onClick={() => { onWelcomeBackDismiss?.(); onOpenDesk?.(); }}
                      style={{ padding: "11px 14px", borderRadius: 10, background: "#4DB8B5", border: "none", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                      Pick up where I left off
                    </button>
                    <button onClick={() => {
                        onWelcomeBackDismiss?.();
                        onStartFresh?.();
                      }}
                      style={{ padding: "11px 14px", borderRadius: 10, background: "white", border: "1.5px solid #4DB8B5", color: "#4DB8B5", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                      Start something new
                    </button>
                  </>
                : showWorldPair && <>
                    <button onClick={() => { onWelcomeBackDismiss?.(); onNavigateToLastRoom?.(); }}
                      style={{ padding: "11px 14px", borderRadius: 10, background: "#4DB8B5", border: "none", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                      Pick up where I left off
                    </button>
                    {/* Swaps this pair for the start-options trio rather than opening the map. */}
                    <button onClick={() => setStartOptionsOpen(true)}
                      style={{ padding: "11px 14px", borderRadius: 10, background: "white", border: "1.5px solid #4DB8B5", color: "#4DB8B5", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                      Start somewhere else
                    </button>
                  </>
              }
            </div>
          );

  const startOptions = showStartOptions && (
            <div style={{ padding: dock ? 0 : "0 12px 10px", display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 8, flexShrink: 0 }}>
              <button onClick={() => { setExplainerMode(false); onBrainDump?.(); }}
                style={{ flex: 1, padding: "9px 6px", borderRadius: 10, background: "#4DB8B5", border: "none", color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                {hasWorld ? "Expand my world" : "Build my world"}
              </button>
              <button onClick={() => {
                  if (!havenIntroSeen) {
                    setHavenIntroSeen(true);
                    setMessages(prev => [...prev, {
                      role: "assistant",
                      content: "Haven is your home base island, where you rest and recharge. It's actually right behind this panel.\n\nYour nervous system needs real rest, not just a break from your task list. That kind of recovery only kicks in when there's no pressure to perform. So there's none here. Rest, learn, play, connect with people you meet along the way. It grows as you do.\n\nIt's here whether or not you've built anything yet. Still becoming what it will be.\n\nAnything you want to know more about, or close this and look around yourself. Come back when you're ready to build your world.",
                      isWelcome: true
                    }]);
                  } else {
                    sendText("Tell me about Haven");
                  }
                }}
                style={{ flex: 1, padding: "9px 6px", borderRadius: 10, background: "white", border: "1.5px solid #4DB8B5", color: "#4DB8B5", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                Tell me about Haven
              </button>
              <button onClick={() => { setExplainerMode(true); sendText("How does this work?"); }}
                style={{ flex: 1, padding: "9px 6px", borderRadius: 10, background: "white", border: "1.5px solid #4DB8B5", color: "#4DB8B5", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                How does this work?
              </button>
            </div>
          );

  const textForm = (
          <form onSubmit={sendChatMessage}
            style={{ padding: "10px 12px", borderTop: dock?.collapsed ? "none" : "1px solid rgba(0,0,0,0.06)", display: "flex", gap: 8, background: dock ? "transparent" : "white", flexShrink: 0, alignItems: "flex-end" }}>
            <textarea
              ref={inputRef}
              value={input}
              rows={1}
              onFocus={() => {
                if (!dock?.collapsed) return;
                focusAfterExpandRef.current = true;
                dock.onExpand?.();
              }}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              onPaste={createPasteFallbackHandler(input, setInput)}
              placeholder="Tell Lumen what you need..."
              style={{ flex: 1, background: dock ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, padding: "8px 12px", color: "#1E293B", fontSize: 13, outline: "none", fontFamily: "'DM Sans', sans-serif", resize: "none", overflowY: "auto", maxHeight: sidePanel ? MINIMIZED_CHAT_INPUT_MAX_HEIGHT : CHAT_INPUT_MAX_HEIGHT, lineHeight: 1.5 }}
            />
            <button type="submit" disabled={!input.trim() || showThinking}
              style={{ padding: "8px 12px", borderRadius: 10, background: input.trim() && !showThinking ? "#4DB8B5" : "rgba(77,184,181,0.2)", border: "none", color: input.trim() && !showThinking ? "white" : "rgba(77,184,181,0.4)", fontSize: 16, fontWeight: 600, cursor: input.trim() ? "pointer" : "default", fontFamily: "'DM Sans', sans-serif", flexShrink: 0, lineHeight: 1 }}>
              ↑
            </button>
          </form>
  );

  const capNotice = capState === "full" ? (
          <div data-lumen-cap="full" style={{ padding: "10px 12px", borderTop: dock?.collapsed ? "none" : "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flexShrink: 0 }}>
            <span style={{ flex: 1, minWidth: 180, fontSize: 12.5, lineHeight: 1.5, color: "#475569", fontFamily: "'DM Sans', sans-serif" }}>
              This chat is full. Continue in a new session and I'll bring the last few messages with me.
            </span>
            <button type="button" onClick={onContinueSession}
              style={{ padding: "8px 14px", borderRadius: 10, background: "#4DB8B5", border: "none", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>
              Continue in new session
            </button>
          </div>
  ) : null;

  const inputForm = capNotice || (capState === "warn" ? (
          <div style={{ flexShrink: 0 }}>
            <div data-lumen-cap="warn" style={{ padding: "6px 14px 0", fontSize: 11.5, color: "#B45309", fontFamily: "'DM Sans', sans-serif" }}>
              This chat is almost full. You can keep going, or start a new session soon.
            </div>
            {textForm}
          </div>
  ) : textForm);

  if (dock) {
    return (
      <LumenDockLayout
        dock={dock}
        roomName={roomName}
        showThinking={showThinking && !isBuildingWorld}
        onBrainDump={onBrainDump ? () => { setExplainerMode(false); onBrainDump(); } : null}
        onMaximize={onMaximize}
        messagesArea={messagesArea}
        worldChips={pairButtons || startOptions || null}
        onChip={(chip) => (chip.send ? sendText(chip.send) : chip.onClick?.())}
        inputForm={inputForm}
      />
    );
  }

  return (
    <div
      style={{ display: "flex", flexDirection: "column", background: "rgba(255,255,255,0.97)", backdropFilter: "blur(16px)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.1)", height, flexShrink: 0, transition: "height 0.1s" }}
    >
      {headerRow}
      {!minimized && (
        <>
          {messagesArea}
          {pairButtons}
          {startOptions}
          {inputForm}
        </>
      )}
    </div>
  );
}

export default LumenCard;